import logger from '../../utils/logger';
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Edit,
  Users,
  Hotel,
  Plane,
  HeartPulse,
  Sun,
  CreditCard,
  FileText,
  ArrowLeft,
  Paperclip,
  Trash2,
  ListChecks,
  PlusCircle,
  MessageSquare,
  Bus,
  FileEdit
} from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useSettings } from '../../utils/SettingsContext';
import ReservationDetailContent from './ReservationDetailContent';
import api from '../../utils/api';
import { RESERVATION_STATUS } from '../../utils/constants';
import LoadingOverlay from '../common/LoadingOverlay';
import ConfirmationModal from '../common/ConfirmationModal';
import PassengerManagementTab from '../Gestion/PassengerManagementTab'; // IMPORT THE CORRECT COMPONENT
import AdvisorActivityPanel from './AdvisorActivityPanel';

const DetailManagement = ({ title, icon, onBack, onSave, children }) => (
    <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
            <button onClick={onBack} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                <ArrowLeft className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                {icon}
                {title}
            </h3>
        </div>
        <div className="space-y-6">
            {children}
        </div>
        {onSave && (
          <div className="mt-8 flex justify-end">
              <button 
                  onClick={onSave}
                  className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition-colors"
              >
                  Guardar Cambios
              </button>
          </div>
        )}
    </div>
);

// THE OLD, LOCAL PassengerForm COMPONENT IS REMOVED

const AttachmentForm = ({ setViewMode, onSaveSuccess, showAlert, attachmentData }) => {
  const { control, register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: { attachments: attachmentData }
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "attachments"
  });

  // Actualizar el formulario cuando attachmentData cambie
  useEffect(() => {
    reset({ attachments: attachmentData });
  }, [attachmentData, reset]);

  const onFileChange = (index, e) => {
    if (e.target.files.length) {
      setValue(`attachments.${index}.file`, e.target.files[0]);
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    const metadata = [];
    
    for (const attachment of data.attachments) {
        const itemMetadata = {
            id: attachment.id,
            title: attachment.title,
            observation: attachment.observation,
            file_name: attachment.file_name,
            file_url: attachment.file_url,
        };

        if (attachment.file instanceof File) {
            formData.append('files', attachment.file);
            itemMetadata.fileName = attachment.file.name;
        }
        metadata.push(itemMetadata);
    }

    formData.append('metadata', JSON.stringify(metadata));
    onSaveSuccess(formData);
  };

  return (
    <DetailManagement
      title="Adjuntar Archivos y Notas"
      icon={<Paperclip className="w-5 h-5" />}
      onBack={() => setViewMode('view')}
      onSave={handleSubmit(onSubmit)}
    >
      {fields.map((field, index) => (
        <div key={field.id} className="p-4 border border-gray-200 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-full">
              <label className="block text-sm font-medium text-gray-700">Título</label>
              <input
                {...register(`attachments.${index}.title`, { required: true })}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Ej: Vouchers de hotel"
              />
            </div>
            <div className="col-span-full">
              <label className="block text-sm font-medium text-gray-700">Observaciones</label>
              <textarea
                {...register(`attachments.${index}.observation`)}
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Observaciones sobre el archivo"
              />
            </div>
            <div className="col-span-full">
              <label className="block text-sm font-medium text-gray-700">Archivo</label>
              <input
                type="file"
                onChange={(e) => onFileChange(index, e)}
                className="w-full mt-1"
              />
              {watch(`attachments.${index}.file_url`) && !(watch(`attachments.${index}.file`) instanceof File) && (
                 <div className="text-sm mt-2">
                    Archivo actual: <a href={watch(`attachments.${index}.file_url`)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{watch(`attachments.${index}.file_name`)}</a>
                 </div>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-red-600 text-sm flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Eliminar Archivo
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => append({ title: '', observation: '', file: null, file_name: '', file_url: '' })}
        className="flex items-center gap-2 px-4 py-2 bg-gray-100 border rounded-lg"
      >
        <PlusCircle className="w-4 h-4" /> Agregar Archivo
      </button>
    </DetailManagement>
  );
};

const ReservationFullDetail = ({ reservation, onClose, onUpdateReservation, onEdit, onRequestChange }) => {
  const [viewMode, setViewMode] = useState('view');
  const contentRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  if (!reservation || !reservation._original) {
    return (
        <motion.div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Error al cargar la reserva</h2>
                <p className="text-gray-600 mb-6">Los datos de la reserva no están disponibles.</p>
                <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg">Cerrar</button>
            </motion.div>
        </motion.div>
    );
  }

  const [attachmentData, setAttachmentData] = useState(reservation._original.reservation_attachments || []);

  // Sincronizar attachmentData cuando la reserva se actualice
  useEffect(() => {
    if (reservation._original?.reservation_attachments) {
      setAttachmentData(reservation._original.reservation_attachments);
    }
  }, [reservation._original?.reservation_attachments]);

  const showAlert = (title, message, type = 'warning') => {
    setAlertInfo({ isOpen: true, title, message, type });
  };

  const handlePassengerSave = async (updatedReservationPayload) => {
    logger.debug('=== RESERVATION FULL DETAIL DEBUG ===');
    logger.debug('handlePassengerSave called');
    logger.debug('Payload:', updatedReservationPayload);
    logger.debug('Has reservation_passengers?', !!updatedReservationPayload?.reservation_passengers);

    setIsSaving(true);

    try {
      // Si tiene pasajeros, usar endpoint específico
      if (updatedReservationPayload && updatedReservationPayload.reservation_passengers) {
        logger.debug('Using passengers endpoint');

        const response = await api.put(`/reservations/${reservation.id}/passengers`, {
          passengers: updatedReservationPayload.reservation_passengers
        });

        logger.debug('Response data:', response.data);

        // Recargar los datos de la reserva para obtener los pasajeros actualizados
        logger.debug('Reloading reservation data...');
        const reloadResponse = await api.get(`/reservations/${reservation.id}`);

        logger.debug('Reloaded reservation with passengers:', reloadResponse.data);

        // Actualizar el estado llamando a onUpdateReservation con los datos frescos
        if (onUpdateReservation) {
          onUpdateReservation(reloadResponse.data);
        }

        setViewMode('view');
        showAlert('Éxito', 'Los pasajeros se han guardado correctamente.', 'success');
      } else {
        throw new Error('No se encontraron datos de pasajeros para guardar');
      }
    } catch (error) {
      logger.error('Error in handlePassengerSave:', error);
      showAlert('Error al Guardar', error.response?.data?.message || error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttachmentSave = async (formData) => {
    setIsSaving(true);
    try {
        const response = await api.post(`/reservations/${reservation.id}/attachments/upsert`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        logger.debug('Attachments saved, response:', response.data);

        // Recargar los datos de la reserva para obtener los adjuntos actualizados
        const reloadResponse = await api.get(`/reservations/${reservation.id}`);

        logger.debug('Reloaded reservation with attachments:', reloadResponse.data);

        // Actualizar el estado de attachmentData con los datos frescos
        if (reloadResponse.data._original?.reservation_attachments) {
            setAttachmentData(reloadResponse.data._original.reservation_attachments);
        }

        // Actualizar el estado llamando a onUpdateReservation con los datos frescos
        if (onUpdateReservation) {
            onUpdateReservation(reloadResponse.data);
        }

        setViewMode('view');
        showAlert('Éxito', 'Los adjuntos se han guardado correctamente.', 'success');
    } catch (error) {
        logger.error('Error in handleAttachmentSave:', error);
        showAlert('Error al Guardar', error.response?.data?.message || error.message);
    } finally {
        setIsSaving(false);
    }
  };

  const handleScrollToSection = (sectionId) => {
    const section = contentRef.current.querySelector(`#${sectionId}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const statusConfig = RESERVATION_STATUS[reservation.status] || { label: reservation.status, bgColor: 'bg-gray-100', textColor: 'text-gray-800' };

  // Obtener el número de factura desde diferentes posibles ubicaciones
  const invoiceNumber =
    reservation.invoiceNumber ??
    reservation.invoice_number ??
    reservation._original?.invoiceNumber ??
    reservation._original?.invoice_number;

  // Obtener el tipo de reserva para determinar qué secciones están activas
  const reservationType = reservation._original.reservation_type || 'all_inclusive';

  // Determinar qué secciones están disponibles según el tipo de reserva
  const showFlights = reservationType === 'all_inclusive' || reservationType === 'flights_only';
  const showHotels = reservationType === 'all_inclusive' || reservationType === 'hotel_only';
  const showTours = reservationType === 'all_inclusive' || reservationType === 'tours_only';
  const showMedical = reservationType === 'all_inclusive' || reservationType === 'medical_assistance';
  const showTransfers = reservationType === 'all_inclusive' || reservationType === 'flights_only' || reservationType === 'hotel_only';

  // Verificar si los servicios están confirmados (a nivel global de la reserva)
  const flightsConfirmed = reservation._original.flight_status_ok === true;
  const hotelsConfirmed = reservation._original.hotel_status_ok === true;
  const toursConfirmed = reservation._original.tours_status_ok === true;
  const medicalConfirmed = reservation._original.assistance_status_ok === true;
  const transfersConfirmed = reservation._original.transfer_status_ok === true;

  // Verificar sincronización de pasajeros
  const checkPassengerSync = () => {
    const baseReservation = reservation._original || reservation;
    const adtRequired = baseReservation.passengers_adt || 0;
    const chdRequired = baseReservation.passengers_chd || 0;
    const infRequired = baseReservation.passengers_inf || 0;

    const passengers = baseReservation.reservation_passengers || [];
    const passengerCounts = { ADT: 0, CHD: 0, INF: 0 };

    passengers.forEach(pax => {
      const type = pax.passenger_type || 'ADT';
      if (passengerCounts[type] !== undefined) {
        passengerCounts[type]++;
      }
    });

    return (
      passengerCounts.ADT === adtRequired &&
      passengerCounts.CHD === chdRequired &&
      passengerCounts.INF === infRequired
    );
  };

  const passengersSynced = checkPassengerSync();

  const menuItems = [
    { id: 'info-basica', label: 'Información Básica', icon: FileText, enabled: true },
    { id: 'pasajeros', label: 'Pasajeros', icon: Users, enabled: true, hasWarning: !passengersSynced },
    { id: 'vuelos', label: 'Vuelos', icon: Plane, enabled: showFlights, isConfirmed: flightsConfirmed },
    { id: 'hoteles', label: 'Hoteles', icon: Hotel, enabled: showHotels, isConfirmed: hotelsConfirmed },
    { id: 'traslados', label: 'Traslados', icon: Bus, enabled: showTransfers, isConfirmed: transfersConfirmed },
    { id: 'tours', label: 'Servicios y Tours', icon: Sun, enabled: showTours, isConfirmed: toursConfirmed },
    { id: 'asistencias', label: 'Asistencia Médica', icon: HeartPulse, enabled: showMedical, isConfirmed: medicalConfirmed },
    { id: 'pago', label: 'Información de Pago', icon: CreditCard, enabled: true },
    { id: 'plan-pagos', label: 'Plan de Pagos', icon: ListChecks, enabled: true },
    { id: 'observaciones', label: 'Observaciones', icon: MessageSquare, enabled: true },
    { id: 'adjuntos', label: 'Documentos Adjuntos', icon: Paperclip, enabled: true },
  ];

  const LeftSideMenu = () => (
    <div className="w-64 bg-gray-50 p-6 flex flex-col border-r border-gray-200 rounded-l-2xl">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex-shrink-0">Secciones</h3>
        <div className="overflow-y-auto pr-2 -mr-2">
            <nav className="space-y-2">
                {menuItems.map(item => {
                  const isConfirmed = item.isConfirmed === true;
                  const hasWarning = item.hasWarning === true;

                  return (
                    <button
                        key={item.id}
                        onClick={() => item.enabled && handleScrollToSection(item.id)}
                        disabled={!item.enabled}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          item.enabled
                            ? 'text-gray-700 hover:bg-gray-200 cursor-pointer'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                          <item.icon className={`w-5 h-5 ${item.enabled ? 'text-gray-600' : 'text-gray-400'}`} />
                          <span>{item.label}</span>
                        </div>
                        {isConfirmed && (
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold" title="Servicio confirmado">
                              ✓
                            </span>
                          </div>
                        )}
                        {hasWarning && (
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold" title="Pasajeros desincronizados">
                              ⚠
                            </span>
                          </div>
                        )}
                    </button>
                  );
                })}
            </nav>
        </div>
    </div>
  );

  const RightActionsMenu = () => (
    <div className="w-64 bg-gray-50 p-6 flex flex-col border-l border-gray-200 rounded-r-2xl">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex-shrink-0">Acciones</h3>
        <div className="space-y-3">
            <button onClick={onEdit} className="w-full flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm">
                <Edit className="w-5 h-5" /> Editar Reserva
            </button>
            <button onClick={() => setViewMode('passengers')} className="w-full flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                <Users className="w-5 h-5" /> Gestionar Pasajeros
            </button>
            <button onClick={() => setViewMode('attachments')} className="w-full flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                <Paperclip className="w-5 h-5" /> Adjuntar Archivos
            </button>
            <button onClick={() => setViewMode('changeRequests')} className="w-full flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                <FileEdit className="w-5 h-5" /> Actividad y Solicitudes
            </button>
        </div>
    </div>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'view':
        return <ReservationDetailContent reservation={reservation} showAlert={showAlert} />;
      case 'passengers':
        return (
          <DetailManagement
            title="Gestionar Pasajeros"
            icon={<Users className="w-5 h-5" />}
            onBack={() => setViewMode('view')}
          >
            <PassengerManagementTab
              reservation={reservation}
              onUpdateReservation={handlePassengerSave}
            />
          </DetailManagement>
        );
      case 'attachments':
        return <AttachmentForm
                    attachmentData={attachmentData}
                    setViewMode={setViewMode}
                    onSaveSuccess={handleAttachmentSave}
                    showAlert={showAlert}
                />;
      case 'changeRequests':
        return (
          <DetailManagement
            title="Actividad y Solicitudes"
            icon={<FileEdit className="w-5 h-5" />}
            onBack={() => setViewMode('view')}
          >
            <AdvisorActivityPanel reservationId={reservation.id} />
          </DetailManagement>
        );
      default:
        return <ReservationDetailContent reservation={reservation} showAlert={showAlert} />;
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AnimatePresence>{isSaving && <LoadingOverlay />}</AnimatePresence>
      <ConfirmationModal 
        isOpen={alertInfo.isOpen}
        title={alertInfo.title}
        message={alertInfo.message}
        onConfirm={() => setAlertInfo({ isOpen: false })}
        confirmText="Aceptar"
        hideCancelButton={true}
        type={alertInfo.type}
        confirmButtonClass={alertInfo.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
      />

      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-[98vw] max-h-[95vh] flex flex-col overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">
                Reserva #{reservation.id}
              </h2>
              {invoiceNumber && (
                <>
                  <span className="text-gray-400">|</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 font-medium">Factura:</span>
                    <span className="text-lg font-semibold text-blue-600">
                      {invoiceNumber}
                    </span>
                  </div>
                </>
              )}
            </div>
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            whileHover={{ scale: 1.1 }}
          >
            <X className="w-6 h-6" />
          </motion.button>
        </div>
        <div className="flex flex-1 overflow-hidden">
            {viewMode === 'view' && <LeftSideMenu />}
            <main ref={contentRef} className="flex-1 overflow-y-auto p-6">
                {renderContent()}
            </main>
            {viewMode === 'view' && <RightActionsMenu />}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReservationFullDetail;
